import React from 'react';
import { IconType, IconBaseProps } from 'react-icons';
import { FaEthereum, FaHardHat, FaReact, FaPython, FaAws, FaNodeJs, FaJava } from 'react-icons/fa';
import {
    SiOpenzeppelin, SiRust, SiTailwindcss, SiTypescript, SiLangchain, SiSolana, SiOpenai,
    SiNextdotjs, SiNestjs, SiMongodb, SiDocker, SiN8N, SiFastapi, SiMysql, SiSolidity,
    SiBitcoin, SiChainlink, SiClaude, SiAnthropic, SiVercel, SiPostgresql
} from 'react-icons/si';
import { TbAnchor } from 'react-icons/tb';
import { GoShieldCheck } from 'react-icons/go';
import { GiFoundryBucket } from 'react-icons/gi';

export interface SkillItem {
    icon: React.ReactNode;
    label: string;
    expertScore: number; // 1-10 rating
}

export interface SkillCategory {
    title: string;
    skills: SkillItem[];
}

// Create React elements as factory functions to avoid JSX in config
const createSkillIcon = (IconComponent: IconType, props?: IconBaseProps) => React.createElement(IconComponent, props);
const createImageIcon = (src: string, alt: string) => React.createElement('img', {
    src,
    alt,
    style: { width: '1em', height: '1em' },
    className: "opacity-100 dark:invert"
});

export const skillsData: SkillCategory[] = [
    {
        title: "skills.categories.aiMachineLearning",
        skills: [
            { icon: createSkillIcon(SiClaude), label: "Claude Code", expertScore: 10 },
            { icon: createSkillIcon(SiAnthropic), label: "MCP", expertScore: 9 },
            { icon: createSkillIcon(SiOpenai), label: "OpenAI API", expertScore: 8 },
            { icon: createSkillIcon(SiN8N), label: "n8n", expertScore: 7 },
            { icon: createSkillIcon(SiLangchain), label: "LangChain", expertScore: 6 },
        ]
    },
    {
        title: "skills.categories.blockchainWeb3",
        skills: [
            { icon: createSkillIcon(SiSolidity), label: "Solidity", expertScore: 9 },
            { icon: createSkillIcon(GiFoundryBucket), label: "Foundry", expertScore: 9 },
            { icon: createSkillIcon(FaHardHat), label: "Hardhat", expertScore: 9 },
            { icon: createSkillIcon(FaEthereum), label: "Ethers.js", expertScore: 9 },
            { icon: createSkillIcon(SiOpenzeppelin), label: "skills.ercStandards", expertScore: 9 },
            { icon: createSkillIcon(GoShieldCheck), label: "skills.contractSecurity", expertScore: 8 },
            { icon: createSkillIcon(SiSolana), label: "Solana", expertScore: 8 },
            { icon: createSkillIcon(TbAnchor), label: "Anchor", expertScore: 8 },
            { icon: createSkillIcon(SiChainlink), label: "Chainlink", expertScore: 7 },
            { icon: createSkillIcon(SiBitcoin), label: "skills.defiRestaking", expertScore: 7 },
            { icon: createImageIcon("/aptos_icon.svg", "Move"), label: "Move (Aptos)", expertScore: 6 },
        ]
    },
    {
        title: "skills.categories.frontendDevelopment",
        skills: [
            { icon: createSkillIcon(SiTypescript), label: "TypeScript", expertScore: 9 },
            { icon: createSkillIcon(FaReact), label: "React", expertScore: 8 },
            { icon: createSkillIcon(SiNextdotjs), label: "Next.js", expertScore: 8 },
            { icon: createSkillIcon(SiTailwindcss), label: "Tailwind CSS", expertScore: 8 },
        ]
    },
    {
        title: "skills.categories.backendDevelopment",
        skills: [
            { icon: createSkillIcon(SiNestjs), label: "NestJS", expertScore: 8 },
            { icon: createSkillIcon(FaNodeJs), label: "Node.js", expertScore: 8 },
            { icon: createSkillIcon(SiFastapi), label: "FastAPI", expertScore: 8 },
            { icon: createSkillIcon(FaPython), label: "Python", expertScore: 8 },
            { icon: createSkillIcon(SiRust), label: "Rust", expertScore: 8 },
            { icon: createSkillIcon(FaJava), label: "Java", expertScore: 7 },
        ]
    },
    {
        title: "skills.categories.databasesDevops",
        skills: [
            { icon: createSkillIcon(SiPostgresql), label: "PostgreSQL", expertScore: 7 },
            { icon: createSkillIcon(SiMysql), label: "MySQL", expertScore: 7 },
            { icon: createSkillIcon(SiMongodb), label: "MongoDB", expertScore: 7 },
            { icon: createSkillIcon(FaAws), label: "AWS", expertScore: 7 },
            { icon: createSkillIcon(SiDocker), label: "Docker", expertScore: 6 },
            { icon: createSkillIcon(SiVercel), label: "Vercel", expertScore: 9 },
        ]
    }
];

/** The short list shown on the home page; the full list lives in the skills dialog. */
export const coreSkillGroups: { title: string; skills: string[] }[] = [
    { title: "skills.categories.aiMachineLearning", skills: ["Claude Code", "MCP", "OpenAI API", "LangChain"] },
    { title: "skills.categories.blockchainWeb3", skills: ["Solidity", "Foundry", "Chainlink", "Solana (Anchor)"] },
    { title: "skills.categories.fullStack", skills: ["TypeScript", "Next.js", "NestJS", "PostgreSQL", "AWS"] },
];

export const isSkillI18nKey = (label: string) => label.startsWith("skills.");
